<template>
  <v-content
    v-if="opportunity && opportunity.organization"
    class="guest opportunity page"
    :class="`organization-${opportunity.organization.id}`"
    :style="
      opportunity.organization.coverImage && $vuetify.breakpoint.mdAndUp
        ? 'margin-top: -64px;'
        : 'margin-top: 0rem;'
    "
  >
    <OpportunityCoverImage
      :opportunity="opportunity"
      :height="coverImageHeight"
      overlay-mode="navbar"
    />

    <v-container
      v-if="opportunity && opportunity.organization"
      class="pt-0"
      style="max-width: 900px;"
    >
      <v-card :class="{ 'card--overlap': opportunity.organization.coverImage }">
        <v-layout column>
          <v-flex d-flex class="text-xs-center card--overlap__logo">
            <div>
              <v-avatar
                :size="avatarSize + 2"
                :color="themes.candidate.light.muted"
              >
                <v-avatar :size="avatarSize">
                  <img :src="logoUrl" alt="avatar" />
                </v-avatar>
              </v-avatar>
            </div>
          </v-flex>

          <v-flex column class="text-xs-center mt-5">
            <v-card-text>
              <div>
                <div class="headline">{{ opportunity.name }}</div>
                <div class="pt-1 subheading">
                  <a
                    @click="
                      $router.push(
                        `/organizations/${opportunity.organization.id}`
                      )
                    "
                    :class="themes.candidate.light.mutedText"
                  >
                    {{ opportunity.organization.name }}
                  </a>
                </div>
              </div>
            </v-card-text>
          </v-flex>

          <v-flex d-flex class="text-xs-center">
            <!-- Quick overview -->
            <v-card-text>
              <v-layout row wrap align-center justify-space-around>
                <div
                  v-if="opportunity.locations && opportunity.locations.length"
                  class="px-1"
                >
                  <v-chip outline class="transparent">
                    <v-icon left class="text-vertical-middle">mdi-city</v-icon>
                    {{ opportunity.locations.length }}&nbsp;
                    {{ opportunity.locations.length | pluralize("Location") }}
                  </v-chip>
                </div>
                <div v-if="opportunity.totalIntake" class="px-1">
                  <v-chip outline class="transparent">
                    <v-icon left class="text-vertical-middle"
                      >mdi-account-multiple-plus</v-icon
                    >
                    {{ opportunity.totalIntake }}&nbsp;positions
                  </v-chip>
                </div>
                <div
                  v-if="
                    opportunity.remuneration &&
                      opportunity.remuneration.currency
                  "
                  class="px-1"
                >
                  <v-chip outline class="transparent">
                    <v-icon left class="text-vertical-middle">mdi-coin</v-icon>
                    {{
                      opportunity.remuneration.amount
                        | currency(opportunity.remuneration.currency)
                    }}
                    {{
                      frequencyDescriptor(opportunity.remuneration.frequency)
                    }}
                  </v-chip>
                </div>
              </v-layout>
            </v-card-text>
          </v-flex>

          <v-flex column class="text-xs-center">
            <!-- Apply -->
            <v-card-text v-if="canApply">
              <ApplicationFormDialog
                v-model="showDialog"
                :opportunity="opportunity"
              />
              <v-btn
                :color="themes.candidate.light.successButton"
                @click="loggedIn ? (showDialog = true) : $emit('signup')"
              >
                {{ opportunity.type == "event" ? "RSVP Now" : "Apply Now" }}
              </v-btn>
              <div
                class="text-xs-center"
                :class="themes.candidate.light.primaryText"
              >
                Closes in {{ timeToClosingDate }}
              </div>
            </v-card-text>
            <v-card-text v-else-if="isOpportunityClosed">
              <v-btn :color="themes.candidate.light.successButton" disabled>
                Applications Closed
              </v-btn>
              <div
                v-if="willOpportunityOpen"
                class="text-xs-center"
                :class="themes.candidate.light.primaryText"
              >
                Opens in {{ timeToOpeningDate }}
              </div>
            </v-card-text>
            <v-card-text v-if="opportunity.organization.listing">
              <v-btn
                small
                flat
                :class="themes.candidate.light.secondaryActionButton"
                :href="
                  `https://listings.leap.ly/listings/${opportunity.organization.listing}/respond`
                "
                target="_blank"
              >
                Get updates from {{ opportunity.organization.name }}
              </v-btn>
            </v-card-text>
          </v-flex>
        </v-layout>
      </v-card>

      <v-card
        class="pt-4 px-4 mt-4 summary-card"
        v-if="opportunity.summary || opportunity.video"
      >
        <v-card-title class="title">
          About {{ opportunity.name }}
        </v-card-title>
        <v-card-text>
          <markdown
            :html="false"
            :source="opportunity.summary"
            class="markdown"
          />
        </v-card-text>
        <v-card-text v-if="opportunity.video">
          <v-img class="pb-3">
            <v-layout justify-center>
              <iframe
                :width="$vuetify.breakpoint.mdAndUp ? 560 : 280"
                :height="$vuetify.breakpoint.mdAndUp ? 315 : 158"
                :src="opportunity.video"
                allowfullscreen
                frameborder="0"
                allow="autoplay; encrypted-media"
              />
            </v-layout>
          </v-img>
        </v-card-text>
      </v-card>

      <v-card
        class="pt-4 mt-4"
        v-if="
          (opportunity.locations && opportunity.locations.length) ||
            opportunity.opensAt ||
            opportunity.closesAt ||
            opportunity.commencesAt
        "
      >
        <v-card-title class="title mx-4">Map</v-card-title>
        <v-img lazy-src="https://picsum.photos/10/6?image=100" height="400px">
          <OpportunityMap
            v-if="!loading"
            :markers="markers"
            height="400px"
            :minZoom="2"
            width="100%"
            @apply="showDialog = true"
          />
        </v-img>
      </v-card>

      <v-card
        class="px-4 pt-4 mt-4"
        v-if="opportunity.offerings && opportunity.offerings[0]"
      >
        <v-card-title class="title">Key Offerings</v-card-title>
        <v-card-text v-if="opportunity.offerings">
          <v-container grid-list-lg fluid class="pa-0">
            <v-layout row wrap align-start justify-start>
              <v-flex
                xs6
                sm4
                md3
                lg2
                v-for="offering in opportunity.offerings"
                :key="`offering-${offering}`"
              >
                <div class="text-xs-center">
                  <div class="pb-2">
                    <v-icon large>mdi-checkbox-marked-circle</v-icon>
                  </div>
                  <div class="caption" style="max-width: 120px; margin: auto;">
                    {{ offering }}
                  </div>
                </div>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>

      <v-card
        class="px-4 pt-4 mt-4"
        v-if="opportunity.perks && opportunity.perks[0]"
      >
        <v-card-title class="title">Perks</v-card-title>
        <v-card-text v-if="opportunity.perks">
          <v-container grid-list-lg fluid class="pa-0">
            <v-layout row wrap align-start justify-start>
              <v-flex
                xs6
                sm4
                md3
                lg2
                v-for="perks in opportunity.perks"
                :key="`perks-${perks}`"
              >
                <div class="text-xs-center">
                  <div class="pb-2">
                    <v-icon large>mdi-check-decagram</v-icon>
                  </div>
                  <div class="caption" style="max-width: 120px; margin: auto;">
                    {{ perks }}
                  </div>
                </div>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>

      <v-card class="px-4 pt-4 mt-4">
        <v-card-title class="title">
          Requirements
          <v-spacer />
          <v-tooltip left color="grey darken-3">
            <v-icon slot="activator">mdi-information-outline</v-icon>
            <span> Required to be eligible to apply for this opportunity.</span>
          </v-tooltip>
        </v-card-title>
        <v-card-text
          v-if="
            profileRequirements.length > 0 ||
              qualificationRequirements.length > 0 ||
              experienceRequirements.length > 0
          "
        >
          <RequirementGroup
            v-for="group in profileRequirements"
            :key="`profile-requirement-${group.id}`"
            :group="group"
            :first="group === profileRequirements[0]"
            :last="
              group === profileRequirements[profileRequirements.length - 1]
            "
          />
          <RequirementGroup
            v-for="group in qualificationRequirements"
            :key="`qualification-requirement-${group.id}`"
            :group="group"
            :first="group === qualificationRequirements[0]"
            :last="
              group ===
                qualificationRequirements[qualificationRequirements.length - 1]
            "
          />
          <RequirementGroup
            v-for="group in experienceRequirements"
            :key="`experience-requirement-${group.id}`"
            :group="group"
            :first="group === experienceRequirements[0]"
            :last="
              group ===
                experienceRequirements[experienceRequirements.length - 1]
            "
          />
        </v-card-text>
        <v-card-text v-else>
          No specific requirements have been set and this opportunity is open to
          anyone to apply.
        </v-card-text>
      </v-card>

      <v-card
        class="px-4 pt-4 mt-4"
        v-if="
          (opportunity.behavioralCompetencies &&
            opportunity.behavioralCompetencies.length > 0) ||
            (opportunity.technicalCompetencies &&
              opportunity.technicalCompetencies.length > 0) ||
            (opportunity.professionalTools &&
              opportunity.professionalTools.length > 0)
        "
      >
        <v-card-title class="title">
          Beneficial Skills
          <v-spacer />
          <v-tooltip bottom color="grey darken-3">
            <v-icon slot="activator">mdi-information-outline</v-icon>
            <span>
              The following skills/tools are beneficial, but not necessary to
              qualify for the opportunity.
            </span>
          </v-tooltip>
        </v-card-title>
        <v-card-text>
          <v-subheader
            v-if="
              opportunity.behavioralCompetencies &&
                opportunity.behavioralCompetencies.length > 0
            "
            class="pl-0"
          >
            Behavioral Competencies
          </v-subheader>
          <v-tooltip
            top
            v-for="bc in opportunity.behavioralCompetencies"
            :key="`trait-${bc.id}`"
          >
            <v-chip
              label
              small
              slot="activator"
              :class="themes.candidate.light.chip"
            >
              {{ bc.name }}
            </v-chip>
            <span>{{ bc.description }}</span>
          </v-tooltip>

          <v-subheader
            v-if="
              opportunity.technicalCompetencies &&
                opportunity.technicalCompetencies.length > 0
            "
            class="pl-0"
          >
            Technical Competencies
          </v-subheader>
          <v-chip
            label
            small
            v-for="tc in opportunity.technicalCompetencies"
            :key="tc"
            :class="themes.candidate.light.chip"
          >
            {{ tc }}
          </v-chip>

          <v-subheader
            v-if="
              opportunity.professionalTools &&
                opportunity.professionalTools.length > 0
            "
            class="pl-0"
          >
            Professional Tools
          </v-subheader>
          <v-chip
            label
            small
            v-for="pt in opportunity.professionalTools"
            :key="pt"
            :class="themes.candidate.light.chip"
          >
            {{ pt }}
          </v-chip>
        </v-card-text>
      </v-card>
    </v-container>
  </v-content>
</template>

<script>
// Components
import ApplicationFormDialog from "@/components/candidate/applications/ApplicationFormDialog";
import Markdown from "vue-markdown";
import OpportunityCoverImage from "@/components/guest/opportunities/OpportunityCoverImage";
import OpportunityMap from "@/components/maps/OpportunityMap";
import RequirementGroup from "@/components/guest/opportunities/RequirementGroup";

// Mixins
import { CheckEligibilities } from "@/mixins/CheckEligibilities.js";
import { OpportunityDates } from "@/mixins/OpportunityDates.js";
import { managedDocumentAttributes } from "@/mixins/managedDocumentAttributes.js";

// Helpers
import _ from "lodash";
import { mapGetters } from "vuex";

// Vue Component
export default {
  mixins: [CheckEligibilities, managedDocumentAttributes, OpportunityDates],

  components: {
    ApplicationFormDialog,
    Markdown,
    OpportunityCoverImage,
    OpportunityMap,
    RequirementGroup
  },

  created() {
    this.$store
      .dispatch("opportunities/fetch", this.$route.params.id)
      .then(() => {
        // if (this.logoUrl) this.$store.dispatch('images/fetch', this.logoUrl)
        this.setDocumentAttributes();
        this.loading = false;

        if (this.role === "candidate")
          this.$store.dispatch("candidate/applications/fetchAll");

        const signup = this.$route.query["s"] !== "0";
        if (signup && this.campaign && !this.loggedIn) this.$emit("signup");
      });
  },

  data() {
    return {
      // Toggles
      loading: true,
      // Model
      locations: [],
      showDialog: false
    };
  },

  computed: {
    ...mapGetters({
      applications: "candidate/applications/all",
      campaign: "campaign/model",
      fullName: "auth/fullName",
      loggedIn: "auth/loggedIn",
      role: "auth/role"
    }),

    hasApplied() {
      return !!this.applications.find(
        a => a.opportunity.id === this.opportunity.id
      );
    },
    canApply() {
      return !this.hasApplied && (this.isReferred || this.isOpportunityOpen);
    },

    logoUrl() {
      return _.get(this, "opportunity.organization.logo");
    },

    logo() {
      return this.$store.state.images.images[this.logoUrl];
    },

    markers() {
      if (!this.opportunity.locations) return [];

      const locations = JSON.parse(JSON.stringify(this.opportunity.locations));
      return locations
        .filter(l => l.latitude && l.longitude)
        .map(l => ({ ...l, opportunity: this.opportunity }));
    },

    opportunity() {
      return this.$store.getters["opportunities/find"](this.$route.params.id);
    },

    coverImageHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "150px";
        case "sm":
          return "250px";
        case "md":
          return "400px";
        case "lg":
          return "500px";
        case "xl":
          return "500px";
        default:
          return "30vh";
      }
    },

    avatarSize() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 65;
        case "sm":
          return 85;
        default:
          return 162;
      }
    },

    profileRequirements() {
      if (this.eligibility && this.eligibility.requirementGroups) {
        return this.eligibility.requirementGroups.filter(
          group => group.type === "ProfileRequirementGroup"
        );
      } else if (this.opportunity.requirementGroups) {
        return this.opportunity.requirementGroups.filter(
          group => group.type === "ProfileRequirementGroup"
        );
      } else {
        return [];
      }
    },

    qualificationRequirements() {
      if (this.eligibility && this.eligibility.requirementGroups) {
        return this.eligibility.requirementGroups.filter(
          group => group.type === "ProfileQualificationRequirementGroup"
        );
      } else if (this.opportunity.requirementGroups) {
        return this.opportunity.requirementGroups.filter(
          group => group.type === "ProfileQualificationRequirementGroup"
        );
      } else {
        return [];
      }
    },

    experienceRequirements() {
      if (this.eligibility && this.eligibility.requirementGroups) {
        return this.eligibility.requirementGroups.filter(
          group => group.type === "ExperienceRequirementGroup"
        );
      } else if (this.opportunity.requirementGroups) {
        return this.opportunity.requirementGroups.filter(
          group => group.type === "ExperienceRequirementGroup"
        );
      } else {
        return [];
      }
    }
  },

  methods: {
    frequencyDescriptor(frequency) {
      return {
        1: "yearly",
        12: "monthly",
        52: "weekly"
      }[frequency];
    },

    setDocumentAttributes() {
      if (
        _.get(this, "opportunity.organization.name") &&
        this.documentAttributes
      ) {
        this.documentAttributes.title = `${this.opportunity.name} – ${this.opportunity.organization.name} – Leaply`;
        this.documentAttributes.description = this.opportunity.blurb
          ? this.opportunity.blurb
          : "";
        this.documentAttributes.image = this.logoUrl
          ? this.logoUrl
          : "https://leap.ly/images/leaply-sig-2016.png";
      } else {
        setTimeout(this.setDocumentAttributes, 100);
      }
    }
  }
};
</script>

<style lang="scss">
.v-dialog--fullscreen .guest.opportunity.page {
  padding-top: 0 !important;
}

.guest.opportunity.page {
  .card--overlap {
    margin-top: 0;

    @media (min-width: 960px) {
      margin-top: -3.9rem;
    }
  }

  .card--overlap__logo {
    position: absolute;
    transform: translate(-50%, -50%);
    left: 50%;

    @media (min-width: 960px) {
      transform: translate(-50%, -70%);
    }
  }

  // Customization for Sanlam
  &.organization-74 {
    .summary-card {
      h3 {
        color: #0075c9;
      }

      ul {
        list-style: none;
        li::before {
          content: "•";
          color: #0075c9;
          font-weight: bold;
          display: inline-block;
          width: 1em;
          margin-left: -2.6em;
        }
      }
    }
  }
}
</style>
